import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AtlasRecord,
  CreateRecordInput,
  ImportJob,
  MapDefinition,
  MapSummary,
  StatesResponse,
  UpdateRecordInput,
  AuditVersion,
  ExportResponse,
} from '@grs-atlas/shared';
import { apiFetch } from './api';

export function useMaps() {
  return useQuery({
    queryKey: ['maps'],
    queryFn: () => apiFetch<{ items: MapSummary[] }>('/maps'),
  });
}

export function useMap(mapId: string | undefined) {
  return useQuery({
    queryKey: ['maps', mapId],
    queryFn: () => apiFetch<MapDefinition>(`/maps/${mapId}`),
    enabled: !!mapId,
  });
}

export function useRecords(
  mapId: string | undefined,
  params: Record<string, string | undefined>,
) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) qs.set(k, v);
  }
  const query = qs.toString();
  return useQuery({
    queryKey: ['records', mapId, query],
    queryFn: () =>
      apiFetch<{ items: AtlasRecord[]; total: number }>(
        `/maps/${mapId}/records${query ? `?${query}` : ''}`,
      ),
    enabled: !!mapId,
  });
}

export function useStates(mapId: string | undefined, params: Record<string, string | undefined>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v && k !== 'state') qs.set(k, v);
  }
  const query = qs.toString();
  return useQuery({
    queryKey: ['states', mapId, query],
    queryFn: () =>
      apiFetch<StatesResponse>(`/maps/${mapId}/states${query ? `?${query}` : ''}`),
    enabled: !!mapId,
  });
}

export function useCreateRecord(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecordInput) =>
      apiFetch<AtlasRecord>(`/maps/${mapId}/records`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['records', mapId] });
      void qc.invalidateQueries({ queryKey: ['states', mapId] });
    },
  });
}

export function useUpdateRecord(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, input }: { recordId: string; input: UpdateRecordInput }) =>
      apiFetch<AtlasRecord>(`/maps/${mapId}/records/${recordId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['records', mapId] });
      void qc.invalidateQueries({ queryKey: ['states', mapId] });
    },
  });
}

export function useArchiveRecord(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recordId: string) =>
      apiFetch<AtlasRecord>(`/maps/${mapId}/records/${recordId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['records', mapId] });
      void qc.invalidateQueries({ queryKey: ['states', mapId] });
    },
  });
}

export function useRecordHistory(mapId: string, recordId: string | undefined) {
  return useQuery({
    queryKey: ['history', mapId, recordId],
    queryFn: () =>
      apiFetch<{ items: AuditVersion[] }>(`/maps/${mapId}/records/${recordId}/history`),
    enabled: !!recordId,
  });
}

export function useRestoreVersion(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, version }: { recordId: string; version: number }) =>
      apiFetch<AtlasRecord>(`/maps/${mapId}/records/${recordId}/history/restore/${version}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['records', mapId] });
      void qc.invalidateQueries({ queryKey: ['history', mapId] });
    },
  });
}

export function useCreateImport(mapId: string) {
  return useMutation({
    mutationFn: (input: { fileName: string; mode?: string }) =>
      apiFetch<ImportJob>(`/maps/${mapId}/imports`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

export function useValidateImport(mapId: string) {
  return useMutation({
    mutationFn: (importId: string) =>
      apiFetch<ImportJob>(`/maps/${mapId}/imports/${importId}/validate`, { method: 'POST' }),
  });
}

export function useCommitImport(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (importId: string) =>
      apiFetch<ImportJob>(`/maps/${mapId}/imports/${importId}/commit`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['records', mapId] });
      void qc.invalidateQueries({ queryKey: ['states', mapId] });
    },
  });
}

export function useExport(mapId: string) {
  return useMutation({
    mutationFn: (body: { filters: Record<string, string>; includeArchived?: boolean }) =>
      apiFetch<ExportResponse>(`/maps/${mapId}/exports`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useUpdateMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mapId, patch }: { mapId: string; patch: Partial<MapDefinition> }) =>
      apiFetch<MapDefinition>(`/maps/${mapId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['maps'] });
      void qc.setQueryData(['maps', data.mapId], data);
    },
  });
}

export function useCreateMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (map: MapDefinition) =>
      apiFetch<MapDefinition>('/maps', { method: 'POST', body: JSON.stringify(map) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['maps'] }),
  });
}
